/**
 * APIキー一覧ページ
 *
 * ユーザーのAPIキーを一覧表示し、管理する画面。
 */

import { Head, Link, router } from '@inertiajs/react'
import { HabitLayout } from '@/components/habits/HabitLayout'
import { Button } from '@/components/ui/button'
import {
  new_settings_api_key_path,
  settings_api_key_path,
  settings_path,
} from '@/lib/routes'
import type { PageProps } from '@/types'

interface ApiKey {
  id: number
  name: string
  prefix: string
  last4: string
  lastUsedAt: string | null
  expiresAt: string | null
  revokedAt: string | null
  createdAt: string
}

interface ApiKeysIndexProps extends PageProps {
  apiKeys: ApiKey[]
}

export default function ApiKeysIndex({ apiKeys }: ApiKeysIndexProps) {
  const handleDelete = (apiKey: ApiKey) => {
    if (
      window.confirm(
        `APIキー「${apiKey.name}」を削除しますか？この操作は取り消せません。`
      )
    ) {
      router.delete(settings_api_key_path(apiKey.id))
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const activeKeys = apiKeys.filter((key) => !key.revokedAt)

  return (
    <>
      <Head title="APIキー | Init Habit" />

      <HabitLayout title="APIキー管理">
        <div className="mx-auto max-w-3xl">
          {/* 戻るリンク */}
          <div className="mb-4">
            <Link
              href={settings_path()}
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              ← 設定に戻る
            </Link>
          </div>

          {/* ヘッダー */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">APIキー</h2>
              <p className="text-muted-foreground text-sm">
                ChatGPTsなどの外部サービスとの連携に使用します
              </p>
            </div>
            <Link href={new_settings_api_key_path()}>
              <Button>新しいキーを作成</Button>
            </Link>
          </div>

          {/* APIキー一覧 */}
          {activeKeys.length === 0 ? (
            <div className="bg-card border-border rounded-xl border p-8 text-center">
              <p className="text-muted-foreground mb-4">
                APIキーがまだありません
              </p>
              <Link href={new_settings_api_key_path()}>
                <Button>最初のキーを作成</Button>
              </Link>
            </div>
          ) : (
            <div className="bg-card border-border divide-border divide-y rounded-xl border">
              {activeKeys.map((apiKey) => (
                <div
                  key={apiKey.id}
                  className="flex items-center justify-between p-4"
                >
                  <div className="space-y-1">
                    <div className="font-medium">{apiKey.name}</div>
                    <div className="text-muted-foreground font-mono text-sm">
                      {apiKey.prefix}...{apiKey.last4}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      作成: {formatDate(apiKey.createdAt)}
                      {apiKey.lastUsedAt && (
                        <> · 最終使用: {formatDate(apiKey.lastUsedAt)}</>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(apiKey)}
                    className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    削除
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* 注意事項 */}
          <div className="bg-muted/50 mt-6 rounded-lg p-4">
            <h3 className="mb-2 font-medium">注意事項</h3>
            <ul className="text-muted-foreground list-inside list-disc space-y-1 text-sm">
              <li>APIキーは作成時に一度だけ表示されます</li>
              <li>キーを紛失した場合は、新しいキーを作成してください</li>
              <li>不要になったキーは削除することをお勧めします</li>
            </ul>
          </div>
        </div>
      </HabitLayout>
    </>
  )
}
